import type { BackendErrorResponse } from '../@types/backend-error-response';

const capturedErrors = new WeakSet<object>();
const ignoreStatusCodes = [403, 401];

function isBackendErrorResponse(value: unknown): value is BackendErrorResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'response' in value &&
    typeof (value as BackendErrorResponse).response?.status === 'number'
  );
}

export function captureException(errorObject: unknown) {
  if (
    typeof errorObject === 'object' &&
    errorObject !== null &&
    capturedErrors.has(errorObject)
  ) {
    return;
  }

  if (typeof errorObject === 'object' && errorObject !== null) {
    capturedErrors.add(errorObject);
  }

  if (isBackendErrorResponse(errorObject)) {
    const { response, request } = errorObject;

    if (ignoreStatusCodes.includes(response.status)) return;

    const { title, detail } = response.error ?? {};
    const operationLabel = request.operationName ?? 'unknown operation';
    const message = [
      `Backend error ${response.status}`,
      operationLabel,
      title,
      detail,
    ]
      .filter(Boolean)
      .join(': ');

    const error = new Error(message);
    import('@sentry/react').then((Sentry) => {
      Sentry.withScope((scope) => {
        scope.setExtras({
          status: response.status,
          operationName: request.operationName,
          title,
          detail,
          query: request.query,
          variables: request.variables,
        });
        Sentry.captureException(error);
      });
    });
    return;
  }

  if (
    !(errorObject instanceof Error) &&
    errorObject !== null &&
    errorObject !== undefined
  ) {
    let message: string;
    try {
      message = JSON.stringify(errorObject);
    } catch {
      message = String(errorObject);
    }
    const error = new Error(message);
    import('@sentry/react').then((Sentry) => {
      Sentry.withScope((scope) => {
        scope.setExtra('originalValue', errorObject);
        Sentry.captureException(error);
      });
    });
    return;
  }

  import('@sentry/react').then((Sentry) => {
    Sentry.captureException(errorObject);
  });
}
